import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { css, cx } from '@linaria/atomic';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { KeyNames } from '../../../constants/key-names';
import { DynamicFieldIconSmall } from '../../../icon/icons-small/dynamic-field-small';
import { NakedButton } from '../../../naked/naked-button.component';
import { Button } from '../../button/button.component';
import { PopoverDialog } from '../../popover/dialog/popover-dialog.component';
import { usePopoverDialog } from '../../popover/dialog/use-popover-dialog';
import { Text } from '../../text/text.component';
import { ActionPressedChip } from '../atoms/action-pressed-chip';
import { useEditorContext } from '../providers/editor-provider';

const dynamicFieldButtonWithOptionsButtonStyle1 = css`
  font-weight: ${theme.font.weight.regular};
  height: ${theme.spacing(3)};
  font-size: ${theme.fontSize(12)};
`;

const dynamicFieldButtonWithOptionsNakedButtonStyle2 = css`
  padding: ${`${theme.spacing(0, 0.5)} !important`};
`;

const dynamicFieldButtonWithOptionsIconStyle3 = css`
  transition: transform 200ms ease-out;
  transform: inherit;
`;

const dynamicFieldButtonWithOptionsIconOpenStyle = css`
  transform: rotate(180deg);
`;

const dynamicFieldButtonWithOptionsPopoverDialogStyle4 = css`
  padding: ${theme.spacing(1, 0)};
`;

const dynamicFieldButtonWithOptionsTextStyle5 = css`
  list-style: none;
  padding: ${theme.spacing(1, 2)};
  cursor: pointer;
`;

const dynamicFieldButtonButtonStyle6 = css`
  font-weight: ${theme.font.weight.regular};
  height: ${theme.spacing(3)};
  font-size: ${theme.fontSize(12)};
`;

const dynamicFieldActionPopoverDialogStyle7 = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing(1)};
  max-width: 400px;
  width: 400px;
  border-radius: ${theme.borderRadius.medium};
  button {
    height: auto;
    width: auto;
    padding: 2px 8px;
  }
`;

type DynamicFieldBaseAttributes = { label: string; symbol: string; val?: string };
export type DynamicFieldAttributes = DynamicFieldBaseAttributes & {
  customSelect?: boolean;
  options?: DynamicFieldBaseAttributes[];
};

const SIDE_PADDING = 28;

export type SharedDynamicFieldsProps = {
  correspondingDynamicFields?: Record<string, DynamicFieldAttributes[]>;
  sharedDynamicFields?: DynamicFieldAttributes[];
  setSharedDynamicFields?: (fields: DynamicFieldAttributes[]) => void;
};

type DynamicFieldButtonProps = {
  field: DynamicFieldAttributes;
  onSelect: (selection: DynamicFieldAttributes) => void;
};

const DynamicFieldButtonWithOptions = ({ field, onSelect }: DynamicFieldButtonProps) => {
  const { sharedDynamicFieldProps = {} } = useEditorContext();
  const { correspondingDynamicFields, setSharedDynamicFields, sharedDynamicFields } = sharedDynamicFieldProps;
  const [selectedDynamicField, setSelectedDynamicField] = useState<DynamicFieldAttributes>(field);
  const popoverRef = useRef<HTMLDialogElement>(null);

  const [offsetX, setOffsetX] = useState(0);
  const offsetY = 24;

  const popover = usePopoverDialog({
    placement: 'bottom-end',
    initialOffset: {
      x: offsetX,
      y: offsetY,
    },
  });

  useEffect(() => {
    const newSelectedDynamicField = sharedDynamicFields?.find((t) => t.symbol === selectedDynamicField.symbol);
    if (newSelectedDynamicField) {
      setSelectedDynamicField(newSelectedDynamicField);
    }
  }, [selectedDynamicField.symbol, sharedDynamicFields]);

  useEffect(() => {
    // Popover dropdown needs to be dynamically offset so it's symmetrically positioned beneath the tag
    if (!popover.isOpen || !popoverRef.current) return;
    const dropdownWidth = popoverRef.current.offsetWidth;
    setOffsetX(-dropdownWidth + SIDE_PADDING);
  }, [popover.isOpen]);

  const handleOptionSelect = (dynamicField: DynamicFieldAttributes) => {
    setSelectedDynamicField(dynamicField);
    if (dynamicField.symbol && correspondingDynamicFields && correspondingDynamicFields?.[dynamicField.label]) {
      setSharedDynamicFields?.(correspondingDynamicFields[dynamicField.label] as DynamicFieldAttributes[]);
    }
    onSelect(dynamicField);
    popover.close();
  };

  return (
    <Fragment key={field.symbol}>
      <Button
        className={dynamicFieldButtonWithOptionsButtonStyle1}
        size='small'
        onClick={() => onSelect(selectedDynamicField)}
        variant='secondary'
      >
        {selectedDynamicField.label}
        <NakedButton
          {...popover.getTriggerProps()}
          onClick={(e) => {
            e.stopPropagation();
            popover.getTriggerProps().onClick?.(e);
          }}
          className={dynamicFieldButtonWithOptionsNakedButtonStyle2}
        >
          <Icon
            name='caret-down-small'
            className={cx(
              dynamicFieldButtonWithOptionsIconStyle3,
              popover?.isOpen && dynamicFieldButtonWithOptionsIconOpenStyle
            )}
          />
        </NakedButton>
      </Button>
      {field?.options ? (
        <PopoverDialog
          {...popover.getDialogProps()}
          className={dynamicFieldButtonWithOptionsPopoverDialogStyle4}
          ref={popoverRef}
        >
          {field.options.map((dynamicField, index) => (
            <Text
              as='li'
              size='small'
              key={index}
              tabIndex={0}
              className={dynamicFieldButtonWithOptionsTextStyle5}
              onClick={() => handleOptionSelect(dynamicField)}
              onKeyDown={(e) => {
                if (e.key === KeyNames.Enter) handleOptionSelect(dynamicField);
              }}
            >
              {dynamicField.label}
            </Text>
          ))}
        </PopoverDialog>
      ) : null}
    </Fragment>
  );
};

const DynamicFieldButton = ({ field, onSelect }: DynamicFieldButtonProps) => (
  <Button
    key={field.symbol}
    className={dynamicFieldButtonButtonStyle6}
    size='small'
    onClick={() => onSelect(field)}
    variant='secondary'
  >
    {field.label}
  </Button>
);

type DynamicFieldActionProps = {
  fields: DynamicFieldAttributes[];
  label?: ReactNode;
  onSelect: (selection: DynamicFieldAttributes) => void;
} & Pick<React.ComponentProps<typeof ActionPressedChip>, 'showBoxShadow' | 'compact' | 'className' | 'isActive'>;

export const DynamicFieldAction = ({
  compact = true,
  fields,
  isActive,
  label,
  onSelect,
  showBoxShadow = true,
  ...rest
}: DynamicFieldActionProps) => {
  const { getDialogProps, getTriggerProps, isOpen } = usePopoverDialog<HTMLButtonElement | HTMLAnchorElement>({
    placement: 'bottom-start',
    middlewareOptions: {
      offset: {
        crossAxis: 0,
        mainAxis: 12,
      },
    },
  });

  return (
    <>
      <ActionPressedChip
        showBoxShadow={showBoxShadow}
        isActive={isOpen || isActive}
        compact={compact}
        {...getTriggerProps()}
        {...rest}
      >
        {label ?? (
          <>
            <DynamicFieldIconSmall style={{ marginRight: theme.spacing(1) }} /> Dynamic Field
          </>
        )}
      </ActionPressedChip>
      <PopoverDialog {...getDialogProps()} returnFocus={false} className={dynamicFieldActionPopoverDialogStyle7}>
        {fields.map((field) =>
          field.options ? (
            <DynamicFieldButtonWithOptions key={field.symbol} field={field} onSelect={onSelect} />
          ) : (
            <DynamicFieldButton key={field.symbol} field={field} onSelect={onSelect} />
          )
        )}
      </PopoverDialog>
    </>
  );
};
